import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { RequirementColumn } from "@/pages/Requirement/type";

import SText from "@/styles/SText";
import { Inventory } from "@/types/inventory";
import CommonTable from "@/components/CommonTable";
import { App, Button, Flex, Form, Input, InputNumber, Spin, Tag } from "antd";
import { SortAscendingOutlined, SortDescendingOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import { MatchedRequirementColumn } from "..";
import { list, realList } from "@/apis/inventoryv2";
import { fInventoryList } from "@/apis/factoryInventory";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import uuid from 'react-uuid';
import { getRequirementRowKey, numFormmater } from "@/pages/Requirement/util";
import { InventoryV2 } from "@/types/inventoryV2";
import { FactoryInventory } from "@/types/factoryInventory";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import ListFilterBar from "@/components/ListFilterBar";
import { filterProductItemsByKeyword } from "@/utils/product";
import { MaterialSearchWrap } from "@/pages/Requirement/style";

interface InventoryFields {
  count: number;
  preIn: number;
  preOut: number;
  useCount: number;
  allocated: number;
};

interface FactoryFields {
  count: number;
  allocated: number;
}

interface MatchStockProps {
  purchaseProducts: Array<RequirementColumn>;
  matchedProducts: Array<MatchedRequirementColumn>;
  setMatchedProducts: Dispatch<SetStateAction<MatchedRequirementColumn[]>>;
  requireCount: number;
  scrollHeight?: number;
  setDisNext: Dispatch<SetStateAction<boolean>>;
  isMerge?: boolean;
  matchFactory?: boolean;
  locTypes?: number[];
}

const MatchStock: FC<MatchStockProps> = ({
  purchaseProducts,
  matchedProducts,
  setMatchedProducts,
  requireCount,
  scrollHeight = 600,
  setDisNext,
  isMerge = false,
  matchFactory = false,
  locTypes = [],
}) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const filteredItems = useMemo(() => filterProductItemsByKeyword(matchedProducts, searchKeyword), [matchedProducts, searchKeyword]);
  const { notification } = App.useApp();
  const [matching, setMatching] = useState<boolean>(false);
  const [macted, setMacted] = useState<boolean>(false);
  const [mergedPros, setMergedPros] = useState<Array<string>>([]);

  const [stockObj, setStockObj] = useState<Record<string, InventoryFields>>({});
  const [realStockObj, setRealStockObj] = useState<Record<string, InventoryFields>>({});
  const [factoryObj, setFactoryObj] = useState<Record<string, FactoryFields>>({});

  const needRealMatch = locTypes.length > 0;

  // 排序状态
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null
  });

  // 排序组件
  const SortHeader = ({
    text,
    sortKey
  }: {
    text: string;
    sortKey: string;
  }) => (
    <Flex
      align="center"
      gap={4}
      style={{ cursor: "pointer", userSelect: "none", flex: 1 }}
      onClick={() => {
        let newDirection: "asc" | "desc" | null = "asc";
        if (sortConfig.key === sortKey) {
          if (sortConfig.direction === "asc" || !!!sortConfig.direction) {
            newDirection = 'desc';
          } else if (sortConfig.direction === "desc") {
            newDirection = 'asc'
          }
        }
        setSortConfig({ key: sortKey, direction: newDirection });
      }}
    >
      <SText text={text} />
      {sortConfig.key === sortKey && sortConfig.direction === "asc" && (
        <SortAscendingOutlined style={{ color: "#1890ff" }} />
      )}
      {sortConfig.key === sortKey && sortConfig.direction === "desc" && (
        <SortDescendingOutlined style={{ color: "#1890ff" }} />
      )}
    </Flex>
  );

  // 处理排序逻辑
  const handleSort = (
    data: MatchedRequirementColumn[],
    key: string,
    direction: "asc" | "desc" | null
  ) => {
    if (!direction) return data;

    return [...data].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (key) {
        case "productCode":
          valueA = Number(a.product?.code ?? "") || 0;
          valueB = Number(b.product?.code ?? "") || 0;
          break;
        case "useAmount":
          valueA = Number(a.useAmount ?? 0);
          valueB = Number(b.useAmount ?? 0);
          break;
        case "stockNum":
          valueA = Number(stockObj[a.product?.code ?? '']?.count ?? 0);
          valueB = Number(stockObj[b.product?.code ?? '']?.count ?? 0);
          break;
        case "stockPreOut":
          valueA = Number(stockObj[a.product?.code ?? '']?.preOut ?? 0);
          valueB = Number(stockObj[b.product?.code ?? '']?.preOut ?? 0);
          break;
        case "preIn":
          valueA = Number(stockObj[a.product?.code ?? '']?.preIn ?? 0);
          valueB = Number(stockObj[b.product?.code ?? '']?.preIn ?? 0);
          break;
        case "useNum":
          valueA = Number(stockObj[a.product?.code ?? '']?.useCount ?? 0);
          valueB = Number(stockObj[b.product?.code ?? '']?.useCount ?? 0);
          break;
        case "allocated":
          valueA = Number(a.allocated ?? 0);
          valueB = Number(b.allocated ?? 0);
          break;
        case "realStockNum":
          valueA = Number(realStockObj[a.product?.code ?? '']?.count ?? 0);
          valueB = Number(realStockObj[b.product?.code ?? '']?.count ?? 0);
          break;
        case "realPreStock":
          valueA = Number(realStockObj[a.product?.code ?? '']?.preOut ?? 0);
          valueB = Number(realStockObj[b.product?.code ?? '']?.preOut ?? 0);
          break;
        case "factoryCount":
          valueA = Number(factoryObj[a.product?.code ?? '']?.count ?? 0);
          valueB = Number(factoryObj[b.product?.code ?? '']?.count ?? 0);
          break;
        case "facStockAlloc":
          valueA = Number(factoryObj[a.product?.code ?? '']?.allocated ?? 0);
          valueB = Number(factoryObj[b.product?.code ?? '']?.allocated ?? 0);
          break;
        case "useFactoryCount":
          valueA = Number(a.useFactoryCount ?? 0);
          valueB = Number(b.useFactoryCount ?? 0);
          break;
        case "purCount":
          valueA = Number(a.purCount ?? 0);
          valueB = Number(b.purCount ?? 0);
          break;
        case "preOutNum":
          valueA = Number(a.preOutNum ?? 0);
          valueB = Number(b.preOutNum ?? 0);
          break;
        default:
          return 0;
      }

      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // 排序 matchedProducts
  useEffect(() => {
    if (sortConfig.direction && sortConfig.key) {
      const sortedItems = handleSort(matchedProducts, sortConfig.key, sortConfig.direction);
      if (JSON.stringify(sortedItems.map(i => i.matchKey ?? i.product?.code)) !== JSON.stringify(matchedProducts.map(i => i.matchKey ?? i.product?.code))) {
        setMatchedProducts(sortedItems);
      }
    }
  }, [sortConfig]);

  useEffect(() => {
    setMacted(false);
    setMergedPros([]);
    const mergePurProducts = isMerge ? uniqueMerge([...purchaseProducts]) : [...purchaseProducts];
    matchStock([...mergePurProducts]);
  }, [requireCount, isMerge]);

  useEffect(() => {
    if (macted && mergedPros.length > 0) {
      notification.info({
        placement: 'top',
        message: t("common.tip"),
        description: t("bom.merge", { codes: mergedPros.join(",") })
      });
    }
  }, [macted, mergedPros]);

  const uniqueMerge = (items: RequirementColumn[]) => {
    const mergeMap = new Map<string, RequirementColumn>();
    let merged: string[] = [];
    items.forEach(item => {
      let proCode = item?.product?.code ?? "";
      if (mergeMap.has(proCode)) {
        const existing = mergeMap.get(proCode)!;

        const currentAmount = parseFloat(String(item?.useAmount ?? 0)) || 0;
        const existingAmount =
          parseFloat(String(existing?.useAmount ?? 0)) || 0;
        const currentBaseQuantity =
          parseFloat(String(item?.baseQuantity ?? 0)) || 0;
        const existingBaseQuantity =
          parseFloat(String(existing?.baseQuantity ?? 0)) || 0;

        const resItem = {
          ...existing,
          useAmount: currentAmount + existingAmount,
          baseQuantity: currentBaseQuantity + existingBaseQuantity
        };
        merged.push(resItem.product.code);
        mergeMap.set(proCode, { ...resItem });
      } else mergeMap.set(proCode, { ...item });
    });
    setMergedPros([...merged]);
    return Array.from(mergeMap.values());
  };

  const matchStock = async (items: RequirementColumn[]) => {
    let stockMap: Record<string, InventoryFields> = {};
    let realStockMap: Record<string, InventoryFields> = {};
    let factoryMap: Record<string, FactoryFields> = {};
    const newValues = [...items];
    const productCodes = newValues.map(nv => nv.product.code);

    // 初始化 stockMap 和 realStockMap
    newValues.forEach(nv => {
      stockMap[nv.product.code] = {
        count: 0,
        preIn: 0,
        preOut: 0,
        useCount: 0,
        allocated: 0
      };
      realStockMap[nv.product.code] = {
        count: 0,
        preIn: 0,
        preOut: 0,
        useCount: 0,
        allocated: 0
      };
      factoryMap[nv.product.code] = {
        count: 0,
        allocated: 0
      };
    });


    setMatching(true);
    setDisNext(true);

    try {
      // 并行请求仓库库存、真实库存和产线库存
      const [stockRes, realStockRes, factoryRes] = await Promise.allSettled([
        list({ body: { ids: productCodes } }),
        needRealMatch ? realList({ body: { ids: productCodes,locationTypes: locTypes } }) : Promise.resolve(null),
        matchFactory ? fInventoryList({ strs: productCodes }) : Promise.resolve(null)
      ]);

      // 处理仓库库存数据
      if (stockRes.status === 'fulfilled') {
        const stockResList: InventoryV2[] = stockRes?.value?.body || [];
        stockResList.forEach(stock => {
          stockMap[stock.product.code] = {
            count: stock.count,
            preIn: stock.preIn,
            preOut: stock.preOut,
            useCount: Math.max(stock.count - stock.preOut, 0),
            allocated: 0
          };
        });
      }
      // 处理真实库存数据
      if (realStockRes?.status === 'fulfilled') {
        const realStockResList: InventoryV2[] = realStockRes?.value?.body || [];
        realStockResList.forEach(stock => {
          realStockMap[stock.product.code] = {
            count: stock.count,
            preIn: stock.preIn,
            preOut: stock.preOut,
            useCount: Math.max(stock.count - stock.preOut, 0),
            allocated: 0
          };
        });
      }
      // 处理产线库存数据
      if (matchFactory && factoryRes.status === 'fulfilled') {
        const factoryResList: FactoryInventory[] = factoryRes?.value?.body || [];
        factoryResList.forEach(factory => {
          factoryMap[factory.product.code] = {
            count: factory.count,
            allocated: 0
          };
        });
      }

      // 生成匹配结果
      let resMatch: MatchedRequirementColumn[] = [];
      newValues.forEach(nv => {
        let isPreReady = (nv.product.preReady || 0) === 1;
        let stockData = stockMap[nv.product.code];
        let realStockData = realStockMap[nv.product.code];
        let factoryData = factoryMap[nv.product.code];

        // 产线使用数量
        let useFactoryCount = Math.min(factoryData.count - factoryData.allocated, nv.useAmount);
        // 仓库预出数量（取 stockData 和 realStockData 的最小值）
        let stockUseCount = stockData.useCount - stockData.allocated;
        let realUseCount = needRealMatch ? (realStockData.useCount - realStockData.allocated) : stockUseCount;
        let useCount = Math.min(stockUseCount, realUseCount);
        let preOutNum = isPreReady ? 0 : Math.min(useCount, nv.useAmount - useFactoryCount);
        // 采购数量 = 需求数量 - 仓库预出数量 - 产线使用数量
        let purCount = isPreReady ? 0 : nv.useAmount - preOutNum - useFactoryCount;

        let matchKey = uuid();
        stockMap[nv.product.code].allocated += preOutNum;
        if (needRealMatch) {
          realStockMap[nv.product.code].allocated += preOutNum;
        }
        factoryMap[nv.product.code].allocated += useFactoryCount;

        let matchedRow: MatchedRequirementColumn = {
          ...nv,
          stockNum: stockData.count,
          stockPreOut: stockData.preOut,
          useNum: stockData.useCount,
          allocated: stockMap[nv.product.code].allocated,
          factoryCount: factoryData.count,
          factoryAllocated: factoryMap[nv.product.code].allocated,
          purCount,
          preOutNum,
          useFactoryCount,
          preIn: stockData.preIn ?? 0,
          matchKey: preOutNum > 0 ? matchKey.replace(/-/g, '') : '',
        };
        resMatch.push(matchedRow);
      });

      setStockObj({ ...stockMap });
      setRealStockObj({ ...realStockMap });
      setFactoryObj({ ...factoryMap });
      setMatchedProducts(resMatch);
      setMacted(true);
    } catch (err) {
      notification.error({ placement: 'top', message: t("bom.matchErr") });
      // 异常时设置为默认值
      const resMatch: MatchedRequirementColumn[] = newValues.map(item => ({
        ...item,
        stockNum: 0,
        stockPreOut: 0,
        useNum: 0,
        allocated: 0,
        factoryCount: 0,
        factoryAllocated: 0,
        purCount: item.product.preReady === 1 ? 0 : item.useAmount,
        preOutNum: 0,
        useFactoryCount: 0,
        preIn: 0,
        matchKey: '',
      }));
      setMatchedProducts(resMatch);
    } finally {
      setDisNext(false);
      setMatching(false);
    }
  };

  const columns: ColumnType<MatchedRequirementColumn>[] = [
    {
      key: "sequence",
      title: <SText text={t("purchase.seq")} />,
      width: 80,
      fixed: "left",
      render: (_, record, index) => index + 1
    },
    {
      key: "productCode",
      title: <SortHeader text={t("product.code")} sortKey="productCode" />,
      dataIndex: "code",
      width: 160,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.code || ""} style={{ color: row.allocated > row.useNum ? "red" : "unset" }} />;
      }
    },
    {
      key: "productName",
      title: <SText text={t("product.name")} />,
      dataIndex: "name",
      width: 140,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.name || ""} />;
      }
    },
    {
      key: "model",
      title: <SText text={t("order.brand")} />,
      width: 340,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.model || ""} />;
      }
    },
    {
      key: "uom",
      title: <SText text={t("product.uom")} />,
      width: 160,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.uom || ""} />;
      }
    },
    {
      key: "preReady",
      title: <SText text={t("product.preReady")} />,
      width: 120,
      render(value, row, rowIndex) {
        return (row?.product?.preReady || 0) === 1 ? (
          <Tag color="#87d068">{t("product.isTrue")}</Tag>
        ) : (
          <Tag color="#f50">{t("product.isFalse")}</Tag>
        );
      }
    },
    {
      key: "useAmount",
      title: <SortHeader text={t("requirement.requireCount")} sortKey="useAmount" />,
      dataIndex: "useAmount",
      width: 160,
      render(value, row, rowIndex) {
        return <SText text={!!row.useAmount ? numFormmater(Number(row.useAmount)) : ''} />
      },
    },
    {
      key: "stockNum",
      title: <SortHeader text={t("bom.stockNum")} sortKey="stockNum" />,
      dataIndex: "stockNum",
      width: 140,
      render(val, row, idx) {
        return <SText text={numFormmater(Number(stockObj[row.product.code]?.count ?? '0'))} />
      }
    },
    {
      key: "stockPreOut",
      title: <SortHeader text={t("bom.preStock")} sortKey="stockPreOut" />,
      dataIndex: "stockPreOut",
      width: 140,
      render(val, row, idx) {
        return <SText text={numFormmater(Number(stockObj[row.product.code]?.preOut ?? '0'))} />
      }
    },
    {
      key: "preIn",
      title: <SortHeader text={t("bom.preInStock")} sortKey="preIn" />,
      dataIndex: "preIn",
      width: 140,
      render(val, row, idx) {
        return <SText text={numFormmater(Number(stockObj[row.product.code]?.preIn ?? '0'))} />
      }
    },
    {
      key: "useNum",
      title: (
        <Flex vertical>
          <SortHeader text={t("bom.useStock")} sortKey="useNum" />
          <SText text={`${t("bom.stockNum")} - ${t("bom.preStock")}`} />
        </Flex>
      ),
      dataIndex: "useNum",
      width: 140,
      render(val, row, idx) {
        let useCount = stockObj[row.product.code]?.useCount || 0;
        return <SText text={numFormmater(Number(useCount || '0'))} />
      }
    },
    {
      key: "allocated",
      title: <SortHeader text={t("bom.allocated")} sortKey="allocated" />,
      dataIndex: "preIn",
      width: 140,
      render(val, row, idx) {
        return <SText text={numFormmater(Number(row.allocated))} />
      }
    },
    //再加四列
    {
      key: "realStockNum",
      title: <SortHeader text={t("bom.realStockNum")} sortKey="realStockNum" />,
      dataIndex: "realStockNum",
      width: 140,
      hidden: !needRealMatch,
      render(val, row, idx) {
        return <SText text={numFormmater(Number(realStockObj[row.product.code]?.count ?? '0'))} />
      }
    },
    {
      key: "realPreStock",
      title: <SortHeader text={t("bom.realPreStock")} sortKey="realPreStock" />,
      dataIndex: "realPreStock",
      width: 140,
      hidden: !needRealMatch,
      render(val, row, idx) {
        return <SText text={numFormmater(Number(realStockObj[row.product.code]?.preOut ?? '0'))} />
      }
    },
    {
      key: "realUseStock",
      title: (
        <Flex vertical>
          <SText text={t("bom.realUseStock")} />
          <SText text={`${t("bom.realStockNum")} - ${t("bom.realPreStock")}`} />
        </Flex>
      ),
      dataIndex: "realUseStock",
      width: 140,
      hidden: true,
      render(val, row, idx) {
        let useCount = realStockObj[row.product.code]?.useCount || 0;
        return <SText text={numFormmater(Number(useCount || '0'))} />
      }
    },
    {
      key: "realAllocated",
      title: <SText text={t("bom.realAllocated")} />,
      dataIndex: "realAllocated",
      width: 140,
      hidden: true,
      render(val, row, idx) {
        return <SText text={numFormmater(Number(realStockObj[row.product.code]?.allocated || '0'))} />
      }
    },
    {
      key: "factoryCount",
      title: (
        <SortHeader text={t("bom.facStock")} sortKey="factoryCount" />
      ),
      dataIndex: "factoryCount",
      width: 140,
      hidden: !matchFactory,
      render(val, row, idx) {
        let count = factoryObj[row.product.code]?.count || 0;
        return <SText text={numFormmater(Number(count || '0'))} />
      }
    },
    {
      key: "facStockAlloc",
      title: <SortHeader text={t("bom.facStockAlloc")} sortKey="facStockAlloc" />,
      dataIndex: "facStockAlloc",
      width: 140,
      hidden: !matchFactory,
      render(val, row, idx) {
        let allocated = factoryObj[row.product.code]?.allocated || 0;
        return <SText text={numFormmater(Number(allocated))} />
      }
    },
    {
      key: "useFactoryCount",
      title: <SortHeader text={t("bom.useFactoryCount")} sortKey="useFactoryCount" />,
      dataIndex: "useFactoryCount",
      width: 140,
      hidden: !matchFactory,
      render(_, row, idx) {
        let value = (row as MatchedRequirementColumn).useFactoryCount || 0;
        return (
          <InputNumber changeOnWheel
            min={0}
            value={value}
            formatter={val => numFormmater(Number(val || 0))}
            max={Math.min((factoryObj[row.product.code]?.count || 0), row.useAmount - row.preOutNum)}
            onChange={val => {
              let newRow = {
                ...row,
                useFactoryCount: val || 0,
                purCount: row.useAmount - row.preOutNum - (val || 0)
              };
              setMatchedProducts(prev => {
                let resValues: MatchedRequirementColumn[] = [...prev.map(p => (p.product.code === newRow.product.code && p.matchKey === newRow.matchKey ? newRow : p))];
                let factoryAllocated = 0;
                resValues.forEach(rv => {
                  if (rv.product.code === row.product.code) factoryAllocated += rv.useFactoryCount
                })
                let newFactoryObj = { ...factoryObj };
                newFactoryObj[row.product.code].allocated = factoryAllocated;
                setFactoryObj({ ...newFactoryObj })
                return resValues.map(rv => ({ ...rv, factoryAllocated: rv.product.code === row.product.code ? factoryAllocated : rv.factoryAllocated }))
              });
            }}
            style={{ width: "100%" }}
          />
        )
      }
    },
    {
      key: "purCount",
      title: (
        <Flex vertical>
          <SortHeader text={t("bom.prePurNum")} sortKey="purCount" />
          <SText
            text={`${t("requirement.requireCount")} - ${t("bom.preOutNum_1")} - ${t("bom.useFactoryCount")}`}
          />
        </Flex>
      ),
      dataIndex: "purCount",
      width: 140,
      fixed: "right",
      render(value, row, rowIndex) {
        return <span style={{ color: row.purCount > 0 ? "red" : "unset" }}>{numFormmater(Number(row.purCount))}</span>
      },
    },
    {
      key: "preOutNum",
      title: (
        <Flex vertical>
          <SortHeader text={t("bom.preOutNum")} sortKey="preOutNum" />
          <SText
            text={`${t("requirement.requireCount")} - ${t("bom.prePurNum_1")}`}
          />
        </Flex>
      ),
      dataIndex: "preOutNum",
      width: 160,
      fixed: "right",
      render: (value, row, rowIndex) => {
        return (
          <InputNumber changeOnWheel
            min={0}
            value={value}
            formatter={val => numFormmater(Number(val || 0))}
            max={Math.min(
              (stockObj[row.product.code]?.useCount || 0),
              needRealMatch ? (realStockObj[row.product.code]?.useCount || 0) : (stockObj[row.product.code]?.useCount || 0),
              row.useAmount - row.useFactoryCount
            )}
            onChange={val => {
              let newRow = {
                ...row,
                preOutNum: val,
                purCount: row.useAmount - val - row.useFactoryCount
              };
              setMatchedProducts(prev => {
                let resValues: MatchedRequirementColumn[] = [...prev.map(p => (p.product.code === newRow.product.code && p.matchKey === newRow.matchKey ? newRow : p))];
                let allocated: number = 0;
                resValues.forEach(rv => {
                  if (rv.product.code === row.product.code) allocated += rv.preOutNum
                })
                let newStockObj = { ...stockObj };
                newStockObj[row.product.code].allocated = allocated;
                setStockObj({ ...newStockObj })
                if (needRealMatch) {
                  let newRealStockObj = { ...realStockObj };
                  newRealStockObj[row.product.code].allocated = allocated;
                  setRealStockObj({ ...newRealStockObj })
                }
                return resValues.map(rv => ({ ...rv, allocated: rv.product.code === row.product.code ? allocated : rv.allocated }))
              }
              );
            }}
            disabled={(row?.product?.preReady || 0) === 1}
            style={{ width: "100%" }}
          />
        )
      }
    }
  ];

  const handleExportExcel = () => {
    const data = matchedProducts.map((item, index) => ({
      [t("purchase.seq")]: index + 1,
      [t("product.code")]: item?.product?.code || "",
      [t("product.name")]: item?.product?.name || "",
      [t("order.brand")]: item?.product?.model || "",
      [t("product.uom")]: item?.product?.uom || "",
      [t("product.preReady")]:
        (item?.product?.preReady || 0) === 1
          ? t("product.isTrue")
          : t("product.isFalse"),
      [t("requirement.requireCount")]: item?.useAmount || 0,
      [t("bom.stockNum")]: item?.stockNum || 0,
      [t("bom.preStock")]: item?.stockPreOut || 0,
      [t("bom.preInStock")]: item?.preIn || 0,
      [t("bom.useStock")]: item?.useNum || 0,
      [t("bom.prePurNum")]: item?.purCount || 0,
      [t("bom.preOutNum")]: item?.preOutNum || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      t("requirement.matchInventory")
    );
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
    });
    saveAs(
      dataBlob,
      `${t("requirement.matchInventory")}_${new Date().getTime()}.xlsx`
    );
  };

  return (
    <div style={{ width: "100%", height: "100%", marginTop: "6px" }}>
      {matching ? (
        <Flex
          style={{ width: "100%", height: scrollHeight - 40 }}
          justify="center"
          align="center"
        >
          <Spin tip={t("requirement.matching")} />
        </Flex>
      ) : (
        <>
          <Flex justify="space-between" gap={6} align="center" style={{ marginBottom: 8 }}>
            <Form.Item label={t('requirement.matchLocType')} style={{ marginBottom: 0, flex: 1, minWidth: 0 }} >
              <SelectLocationType value={locTypes} mode="multiple" maxTagCount={2} />
            </Form.Item>
            <MaterialSearchWrap>
              <ListFilterBar onSearch={() => setSearchKeyword(keyword)} onReset={() => { setKeyword(""); setSearchKeyword(""); }} showReset={false}>
                {({ submit }) => (
                  <Input allowClear value={keyword} onChange={event => setKeyword(event.target.value)} onPressEnter={submit}
                    placeholder={`${t("product.code")} / ${t("product.name")} / ${t("product.model")}`}
                    aria-label={t("requirement.searchPro")} />
                )}
              </ListFilterBar>
            </MaterialSearchWrap>
            <Button
              onClick={handleExportExcel}
              disabled={matchedProducts.length === 0}
            >
              {t("requirement.exportMatchResult")}
            </Button>
          </Flex>
          <CommonTable
            rowKey={row => row.matchKey || getRequirementRowKey(row)}
            columns={columns}
            dataSource={filteredItems}
            scrollHeight={scrollHeight - 80}
          />
        </>
      )}
    </div>
  );
};

export default MatchStock;
